import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/services/api';
import { FormField, Modal, ConfirmDialog, Skeleton } from '@connor-music/shared';
import type { Platform, PlatformCreate } from '@shared/types';
import { Settings, AlertCircle, Plus, Pencil, Trash2, Music } from 'lucide-react';

type SettingsTab = 'platforms';

export default function LeadershipSettings() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('platforms');

  const tabs = [
    { key: 'platforms' as SettingsTab, label: 'Supported Platforms', icon: Music },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/50">
          <Settings className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Settings
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage supported music platforms and other settings.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-6 sm:space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.key
                  ? 'border-accent-500 text-accent-600 dark:text-accent-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'platforms' && <PlatformsSection />}
    </div>
  );
}

// =============================================================================
// Supported Music Platforms
// =============================================================================

function PlatformsSection() {
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState<Platform | null>(null);
  const [deletingPlatform, setDeletingPlatform] = useState<Platform | null>(null);

  const loadPlatforms = useCallback(async () => {
    try {
      const data = await apiClient.getPlatforms();
      setPlatforms(data);
    } catch (err) {
      console.error('Failed to load platforms:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlatforms();
  }, [loadPlatforms]);

  const handleCreate = async (data: PlatformCreate) => {
    await apiClient.createPlatform(data);
    setShowModal(false);
    await loadPlatforms();
  };

  const handleUpdate = async (data: PlatformCreate) => {
    if (!editingPlatform) return;
    await apiClient.updatePlatform(editingPlatform.id, data);
    setEditingPlatform(null);
    await loadPlatforms();
  };

  const handleDelete = async () => {
    if (!deletingPlatform) return;
    await apiClient.deletePlatform(deletingPlatform.id);
    setDeletingPlatform(null);
    await loadPlatforms();
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div className="space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-3 w-64" />
          </div>
          <Skeleton className="h-8 w-28 rounded-md" />
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded" />
                <Skeleton className="h-4 w-32" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-7 w-7 rounded" />
                <Skeleton className="h-7 w-7 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Supported Platforms */}
      <div className="rounded-lg border bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Supported Platforms
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Streaming services available for audit projects.
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600"
          >
            <Plus className="h-4 w-4" />
            Add Platform
          </button>
        </div>

        {platforms.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Music className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <p className="mt-3 text-sm font-medium text-gray-900 dark:text-white">No platforms configured</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Add streaming service platforms to associate with audit projects.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {platforms.map((platform) => (
              <div key={platform.id} className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center gap-4 min-w-0">
                  {/* Logo mark previews */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {platform.logo_mark_light_url ? (
                      <div className="rounded border border-gray-200 bg-white p-1.5 dark:border-gray-600">
                        <img src={platform.logo_mark_light_url} alt="" className="h-6 w-6 object-contain" />
                      </div>
                    ) : (
                      <div className="rounded border border-gray-200 bg-gray-50 p-1.5 dark:border-gray-600 dark:bg-gray-700">
                        <Music className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                    {platform.logo_mark_dark_url && (
                      <div className="rounded border border-gray-700 bg-gray-900 p-1.5">
                        <img src={platform.logo_mark_dark_url} alt="" className="h-6 w-6 object-contain" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {platform.name}
                    </p>
                    {(platform.logo_light_url || platform.logo_dark_url) && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {[platform.logo_light_url && 'Logo', platform.logo_dark_url && 'Logo (dark)']
                          .filter(Boolean)
                          .join(' + ')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setEditingPlatform(platform)}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300 transition-colors"
                    title="Edit platform"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeletingPlatform(platform)}
                    className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors"
                    title="Delete platform"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Add Platform"
      >
        <PlatformForm
          onSubmit={handleCreate}
          onCancel={() => setShowModal(false)}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingPlatform}
        onClose={() => setEditingPlatform(null)}
        title="Edit Platform"
      >
        {editingPlatform && (
          <PlatformForm
            platform={editingPlatform}
            onSubmit={handleUpdate}
            onCancel={() => setEditingPlatform(null)}
          />
        )}
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={!!deletingPlatform}
        onCancel={() => setDeletingPlatform(null)}
        onConfirm={handleDelete}
        title="Delete Platform"
        message={`Are you sure you want to delete "${deletingPlatform?.name}"? Existing projects linked to this platform will retain their association but the logo will no longer display.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}

// =============================================================================
// Platform Form (used for both Create and Edit)
// =============================================================================

function PlatformForm({
  platform,
  onSubmit,
  onCancel,
}: {
  platform?: Platform;
  onSubmit: (data: PlatformCreate) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(platform?.name ?? '');
  const [logoLightUrl, setLogoLightUrl] = useState(platform?.logo_light_url ?? '');
  const [logoDarkUrl, setLogoDarkUrl] = useState(platform?.logo_dark_url ?? '');
  const [logoMarkLightUrl, setLogoMarkLightUrl] = useState(platform?.logo_mark_light_url ?? '');
  const [logoMarkDarkUrl, setLogoMarkDarkUrl] = useState(platform?.logo_mark_dark_url ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const inputClasses =
    'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-primary-400 dark:focus:ring-primary-400';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSubmitting(true);
    setError('');
    try {
      await onSubmit({
        name: name.trim(),
        logo_light_url: logoLightUrl.trim() || undefined,
        logo_dark_url: logoDarkUrl.trim() || undefined,
        logo_mark_light_url: logoMarkLightUrl.trim() || undefined,
        logo_mark_dark_url: logoMarkDarkUrl.trim() || undefined,
      });
    } catch (err) {
      console.error('Failed to save platform:', err);
      setError('Failed to save platform.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <FormField label="Service Name" required htmlFor="platform-name">
        <input
          id="platform-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputClasses}
          placeholder="e.g., Spotify"
          required
        />
      </FormField>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Logo (Light Theme)" htmlFor="platform-logo-light">
          <input
            id="platform-logo-light"
            type="url"
            value={logoLightUrl}
            onChange={(e) => setLogoLightUrl(e.target.value)}
            className={inputClasses}
            placeholder="https://..."
          />
        </FormField>

        <FormField label="Logo (Dark Theme)" htmlFor="platform-logo-dark">
          <input
            id="platform-logo-dark"
            type="url"
            value={logoDarkUrl}
            onChange={(e) => setLogoDarkUrl(e.target.value)}
            className={inputClasses}
            placeholder="https://..."
          />
        </FormField>

        <FormField label="Logo Mark (Light Theme)" htmlFor="platform-mark-light">
          <input
            id="platform-mark-light"
            type="url"
            value={logoMarkLightUrl}
            onChange={(e) => setLogoMarkLightUrl(e.target.value)}
            className={inputClasses}
            placeholder="https://..."
          />
        </FormField>

        <FormField label="Logo Mark (Dark Theme)" htmlFor="platform-mark-dark">
          <input
            id="platform-mark-dark"
            type="url"
            value={logoMarkDarkUrl}
            onChange={(e) => setLogoMarkDarkUrl(e.target.value)}
            className={inputClasses}
            placeholder="https://..."
          />
        </FormField>
      </div>

      {/* Preview */}
      {(logoMarkLightUrl || logoMarkDarkUrl) && (
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Logo Mark Preview
          </p>
          <div className="flex items-center gap-3">
            {logoMarkLightUrl && (
              <div className="rounded-md bg-white border border-gray-200 p-2 dark:border-gray-600">
                <img src={logoMarkLightUrl} alt="Mark light preview" className="h-8 w-8 object-contain" />
              </div>
            )}
            {logoMarkDarkUrl && (
              <div className="rounded-md bg-gray-900 border border-gray-700 p-2">
                <img src={logoMarkDarkUrl} alt="Mark dark preview" className="h-8 w-8 object-contain" />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 transition-colors dark:bg-primary-500 dark:hover:bg-primary-600"
        >
          {isSubmitting ? 'Saving...' : platform ? 'Update Platform' : 'Add Platform'}
        </button>
      </div>
    </form>
  );
}
